import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useManagerDashboard } from '../../../hooks/useDashboard';
import { Card } from '../../../components/common/Card';
import { Icons } from '../../../components/common/Icons';
import { Button } from '../../../components/common/Button';
import { DonutChart, BarChart } from '../../../components/common/Charts';
import { getGreeting, getFirstName, formatDate } from '../../../utils/helpers';
import apiClient from '../../../api/apiClient';
import type { RedFlag, PendingApprovalItem, ManagerActivityItem } from '../../../types';
import './ManagerDashboard.css';

const PROJECT_TYPE_LABELS: Record<string, string> = {
  adm: 'ADM',
  tnm: 'T&M',
  staff_aug: 'Staff Aug',
  inhouse: 'In-house',
  digital_marketing: 'Digital Marketing',
  upwork: 'Upwork',
  outsource: 'Outsource',
  unknown: 'Other',
};

const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  new_project: <Icons.Folder />,
  new_resource: <Icons.UserPlus />,
  ending_soon: <Icons.Calendar />,
};

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(dateStr);
}

export function ManagerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useManagerDashboard();
  const [actionLoading, setActionLoading] = useState<Record<number, string>>({});

  const [actionError, setActionError] = useState<string | null>(null);

  const handleApprove = useCallback(async (timesheetId: number) => {
    setActionError(null);
    setActionLoading(prev => ({ ...prev, [timesheetId]: 'approve' }));
    try {
      await apiClient.put(`/timesheets/${timesheetId}/approve`, {});
      refetch();
    } catch (err) {
      console.error('Approve failed:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to approve timesheet');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[timesheetId];
        return next;
      });
    }
  }, [refetch]);

  const handleReject = useCallback(async (timesheetId: number) => {
    setActionError(null);
    setActionLoading(prev => ({ ...prev, [timesheetId]: 'reject' }));
    try {
      await apiClient.put(`/timesheets/${timesheetId}/reject`, {});
      refetch();
    } catch (err) {
      console.error('Reject failed:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to reject timesheet');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[timesheetId];
        return next;
      });
    }
  }, [refetch]);

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.AlertCircle />
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={refetch}>Try Again</button>
      </div>
    );
  }

  if (!data || !user) {
    return (
      <div className="manager-dashboard">
        <header className="dashboard-header-content">
          <div className="header-greeting">
            <h1>{getGreeting()}, {user?.name ? getFirstName(user.name) : 'Manager'}!</h1>
            <p>Loading your dashboard...</p>
          </div>
        </header>
        {isLoading && <div className="mgr-loading"><div className="mgr-spinner" /></div>}
      </div>
    );
  }

  const billableTotal = data.billableHoursThisMonth || 0;
  const unbillableTotal = data.unbillableHoursThisMonth || 0;
  const billablePct = billableTotal + unbillableTotal > 0
    ? Math.round((billableTotal / (billableTotal + unbillableTotal)) * 100) : 0;

  // Separate red flags by severity
  const highFlags = data.redFlags?.filter(f => f.severity === 'high') || [];
  const mediumFlags = data.redFlags?.filter(f => f.severity === 'medium') || [];
  const lowFlags = data.redFlags?.filter(f => f.severity === 'low') || [];
  const criticalFlags = [...highFlags, ...mediumFlags];
  const allFlags = [...criticalFlags, ...lowFlags];

  const projectTypeSlices = (data.projectTypeDistribution || []).map((d, i) => ({
    value: d.count,
    color: PIE_COLORS[i % PIE_COLORS.length],
    label: PROJECT_TYPE_LABELS[d.type] || d.type,
  }));

  const statCards = [
    { title: 'My Projects', value: String(data.totalProjects), icon: <Icons.Folder />, color: '#8b5cf6', link: '/projects' },
    { title: 'Pending Approvals', value: String(data.pendingApprovals), icon: <Icons.Check />, color: '#f59e0b', link: '/approvals' },
    { title: 'Billable Hours', value: billableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 }), icon: <Icons.TrendingUp />, color: '#10b981', sub: 'This month', link: '/my-project-team' },
    { title: 'Non-Billable Hours', value: unbillableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 }), icon: <Icons.TrendingDown />, color: '#ef4444', sub: 'This month', link: '/my-project-team' },
  ];

  return (
    <div className="manager-dashboard">
      {/* ── Header ── */}
      <header className="dashboard-header-content">
        <div className="header-greeting">
          <h1>{getGreeting()}, {getFirstName(user.name)}!</h1>
          <p>Here's your project management overview.</p>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate('/log-time')} variant="secondary">
            <Icons.Clock /> Fill Timesheet
          </Button>
          <Button onClick={() => navigate('/approvals')}>
            <Icons.Check /> Approvals
          </Button>
        </div>
      </header>

      {/* ── Stat Cards ── */}
      <section className="stats-grid">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" padding="none" hoverable onClick={() => stat.link && navigate(stat.link)}>
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-details">
                <span className="stat-title">{stat.title}</span>
                <span className="stat-value">{stat.value}</span>
                {stat.sub && <span className="stat-period">{stat.sub}</span>}
              </div>
              </div>
          </Card>
        ))}
      </section>

      {/* ── Work Requests Banner ── */}
      {(data.pendingWorkRequests ?? 0) > 0 && (
        <Card className="mgr-work-requests-banner" padding="none" hoverable onClick={() => navigate('/work-requests')}>
          <div className="mgr-wr-banner-inner">
            <div className="mgr-wr-banner-icon"><Icons.Hand /></div>
            <div className="mgr-wr-banner-text">
              <strong>{data.pendingWorkRequests} team member{data.pendingWorkRequests !== 1 ? 's' : ''}</strong> {data.pendingWorkRequests !== 1 ? 'have' : 'has'} requested work assignment
            </div>
            <Button variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate('/work-requests'); }}>
              Review <Icons.ArrowRight />
            </Button>
          </div>
        </Card>
      )}

      {/* ── Row 1: Red Flags + Pending Approvals ── */}
      <div className="mgr-top-row">
        {/* Red Flags */}
        <Card title="" className="mgr-redflags-card" padding="none">
          <div className="mgr-card-body">
            <div className="red-flags-header">
              <div className="red-flags-title-row">
                <Icons.AlertTriangle />
                <span>Red Flags</span>
              </div>
              <div className="red-flags-badges">
                {highFlags.length > 0 && <span className="red-flags-badge severity-high">{highFlags.length} Critical</span>}
                {mediumFlags.length > 0 && <span className="red-flags-badge severity-medium">{mediumFlags.length} Warning</span>}
                {lowFlags.length > 0 && <span className="red-flags-badge severity-low">{lowFlags.length} Info</span>}
              </div>
            </div>
            {allFlags.length > 0 ? (
              <>
                <div className="red-flags-list">
                  {allFlags.map((flag, idx) => (
                    <div key={idx} className={`red-flag-item severity-${flag.severity}`}>
                      <div className={`red-flag-dot severity-${flag.severity}`} />
                      <div className="red-flag-content">
                        <span className="red-flag-title">{flag.title}</span>
                        <span className="red-flag-detail">{flag.detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state compact">
                <Icons.CheckCircle />
                <p>No red flags — all projects on track!</p>
              </div>
            )}
          </div>
        </Card>

        {/* Pending Approvals */}
        <Card title={`Pending Approvals${data.pendingApprovals ? ` (${data.pendingApprovals})` : ''}`} className="mgr-approvals-card" padding="none">
          <div className="mgr-card-body">
            {actionError && (
              <div className="mgr-action-error">
                <Icons.AlertCircle />
                <span>{actionError}</span>
                <button onClick={() => setActionError(null)} type="button">&times;</button>
              </div>
            )}
            {data.pendingApprovalsList?.length ? (
              <>
                <div className="mgr-approvals-list">
                  {data.pendingApprovalsList.slice(0, 5).map((item: PendingApprovalItem) => {
                    const loading = actionLoading[item.id];
                    return (
                      <div key={item.id} className="mgr-approval-item">
                        <div className="mgr-approval-info">
                          <span className="mgr-approval-name">{item.employeeName}</span>
                          <span className="mgr-approval-detail">
                            {item.projectName} &middot; {formatDate(item.date)}
                            {item.description && ` — ${item.description}`}
                          </span>
                        </div>
                        <span className="mgr-approval-hours">{item.hours}h</span>
                        <div className="mgr-approval-actions">
                          <button type="button" className="mgr-btn-approve" title="Approve" onClick={() => handleApprove(item.id)} disabled={!!loading}>
                            <Icons.Check />
                          </button>
                          <button type="button" className="mgr-btn-reject" title="Reject" onClick={() => handleReject(item.id)} disabled={!!loading}>
                            <Icons.X />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button className="view-all-btn" onClick={() => navigate('/approvals')}>
                  View All Approvals <Icons.ChevronRight />
                </button>
              </>
            ) : (
              <div className="empty-state">
                <Icons.Check />
                <p>No pending approvals</p>
                <span>All timesheets are up to date</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Row 2: Charts — Billable Donut + Monthly Trend ── */}
      <div className="mgr-charts-row">
        <Card title="Billable vs Non-Billable" className="mgr-chart-card">
          <DonutChart
            slices={[
              { value: billableTotal, color: '#10b981', label: `Billable (${billableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}h)` },
              { value: unbillableTotal, color: '#f59e0b', label: `Non-Billable (${unbillableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}h)` },
            ]}
            label={`${billablePct}%`}
            subLabel="Billable"
          />
        </Card>
        <Card title="Monthly Hours Trend" className="mgr-chart-card mgr-bar-chart-card">
          {data.hoursTrend?.length > 0
            ? <BarChart data={data.hoursTrend} />
            : <div className="chart-empty"><Icons.Chart /><p>No trend data available</p></div>}
        </Card>
      </div>

      {/* ── Row 3: Projects Overview + Project Types + Activity ── */}
      <div className="mgr-three-col-row">
        {/* Projects Overview */}
        <Card title={`My Projects (${data.totalProjects})`} className="mgr-projects-card" padding="none">
          <div className="mgr-card-body">
            {data.projectsOverview?.length ? (
              <>
                <div className="mgr-projects-list">
                  {data.projectsOverview.map((p) => {
                    const budget = p.monthlyBudget;
                    const pct = budget ? Math.min(100, Math.round((p.totalHoursThisMonth / budget) * 100)) : null;
                    const barColor = pct !== null ? (pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981') : '#3b82f6';
                    return (
                      <div key={p.projectId} className="mgr-project-row">
                        <div className="mgr-project-info">
                          <span className="mgr-project-name">{p.projectName}</span>
                          <span className="mgr-project-meta">
                            <span className={`mgr-type-badge type-${p.projectType}`}>
                              {PROJECT_TYPE_LABELS[p.projectType] || p.projectType}
                            </span>
                            <span className={`mgr-billable-badge ${p.isBillable ? 'billable' : 'non-billable'}`}>
                              {p.isBillable ? 'Billable' : 'Non-billable'}
                            </span>
                            {p.clientName && <span>{p.clientName}</span>}
                          </span>
                        </div>
                        {budget ? (
                          <div className="mgr-project-bar-area">
                            <div className="mgr-project-bar">
                              <div className="mgr-project-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                            </div>
                            <span className="mgr-project-pct" style={{ color: barColor }}>{pct}%</span>
                          </div>
                        ) : null}
                        <span className="mgr-project-hours">{p.totalHoursThisMonth.toLocaleString(undefined, { maximumFractionDigits: 0 })}h</span>
                        <span className="mgr-project-team"><Icons.Users /> {p.teamCount}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <Icons.Folder />
                <p>No active projects</p>
                <span>Projects you manage will appear here</span>
              </div>
            )}
          </div>
        </Card>

        {/* Project Types Donut */}
        <Card title="Project Types" className="mgr-chart-card mgr-types-card">
          <DonutChart
            slices={projectTypeSlices}
            size={150}
            label={String(data.totalProjects)}
            subLabel="Projects"
          />
        </Card>

        {/* Activity Feed */}
        <Card title="Activity & Alerts" className="mgr-activity-card" padding="none">
          <div className="mgr-card-body">
            {data.activities?.length ? (
              <div className="mgr-activity-list">
                {data.activities.slice(0, 8).map((item: ManagerActivityItem) => (
                  <div key={item.id} className="mgr-activity-item">
                    <div className={`mgr-activity-icon type-${item.type}`}>
                      {ACTIVITY_ICONS[item.type] || <Icons.Activity />}
                    </div>
                    <div className="mgr-activity-content">
                      <span className="mgr-activity-msg">{item.message}</span>
                      <span className="mgr-activity-detail">{item.detail}</span>
                    </div>
                    <span className="mgr-activity-time">{timeAgo(item.timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Icons.Activity />
                <p>No recent activity</p>
                <span>New projects, resources & reminders appear here</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ManagerDashboard;
